import React, { useEffect, useRef } from 'react';
import stores from 'cd-eft/models/Stores';
import { observer } from 'mobx-react';
import { DynamicSingleCSProfile } from 'cd-eft/components/common/DynamicProfile/DynamicSingleCrossSectionProfile';
import { DynamicCrossSectionHub } from 'cd-eft/models/Stores/DynamicProfile/CrossSection/DynamicCrossSectionHub';
import { ProfileStoreType } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileEnums';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { DynamicCrossSectionMapStore } from 'cd-eft/models/Stores/DynamicProfile/CrossSection/DynamicCrossSectionMapStore';
import { CrossSectionVM } from './SingleCrossSectionVM';
import { DynamicTableStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicTableStoreBase';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import './crosssection.scss';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { Table, CardBody, Card } from 'reactstrap';
import Icon from '@mdi/react';
import { nodesLink24 } from "@esri/calcite-ui-icons";
import { mdiClose } from '@mdi/js';
import { Alert } from 'cd-eft/components/common/Alert';
import { Popup } from 'cd-eft/components/common/Popup';
import { LoadingSpinner } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';

const { designStore, crossSectionDesignStore } = stores.AppStore;

const SingleCrossSectionComponent = (props) => {
    let profileHub: DynamicCrossSectionHub;

    const profileStoreRef = useRef<DynamicProfileStoreBase>();
    const profileTableStoreRef = useRef<DynamicTableStoreBase>();

    if (!profileTableStoreRef.current && !profileStoreRef.current) {
        profileHub = new DynamicCrossSectionHub();
        profileHub.createStore(ProfileStoreType.MAP, { mapView: EngineeringMapService.instance?.mapView });
        profileHub.createStore(ProfileStoreType.PROFILE);
        profileStoreRef.current = profileHub.ProfileStore;
        profileHub.createStore(ProfileStoreType.TABLE);
        profileTableStoreRef.current = profileHub.TableStore;
    }

    const vm = useRef(new CrossSectionVM({ ...props, profileTableStore: profileTableStoreRef.current })).current;
    const currentObject = crossSectionDesignStore.crossSectionObject;

    useEffect(() => {
        vm?.onMount();
        function cleanup() {
            profileHub.MapStore?.removeMapLayer('cross section'); // This is temporary, removes the cross section graphic from map when component closes.
            crossSectionDesignStore.resetCurrentObject();
            vm.onUnmount();
        }
        return cleanup;
    }, []);

    return (
        <div className="scs-body-container">
            {/* popups */}
            {vm.thresholdSize &&
                <Popup
                    trigger={vm.thresholdSize}
                    setTrigger={(value: boolean) => { vm.saveReportClicked = value }}
                    title=""
                    handleOK={() => vm.handleConfirmAction(profileStoreRef.current?._profileView, EngineeringMapService.instance?.mapView)}
                    okText="Confirm"
                    handleCancel={vm.handleCancelAction}
                    cancelText="Close"
                >
                    {vm.popupWarningMessage}
                </Popup>
            }

            <div id='scs-header'>
                <h1 className="h5 mt-1 mb-1 ml-1">Single Cross Section</h1>
                <button className="single-crosssection-close" aria-label="close tool" onClick={() => designStore.hideSingleCrossSection = true}>
                    <Icon path={mdiClose} title="Close" size={1} />
                </button>
            </div>
            <Card className="scs-body">
                <CardBody className="scs-card-body">
                    <Accordion className="pb-0 pl-1 pr-1" headers={['Cross Section']} expanded={true} >
                        <>
                            <DynamicSingleCSProfile profileStore={profileStoreRef.current} editable={false} />
                            <div className='profile-body'>
                                <button className='usa-button digitize-btn' onClick={vm.sketchCrossSection} disabled={!designStore.structureLayerExists}>
                                    <Icon path={nodesLink24} title="Digitize" size={1.5} className="esri-collapse__icon esri-icon-public mr-1" />
                                    Digitize
                                </button>
                            </div>
                        </>
                    </Accordion>
                    <Accordion className='crossSection-table-header' headers={['Table']} expanded={true} >
                        <div className="crossSection-table-container">
                            <Table className="crossSection-table">
                                <thead>
                                    <tr>
                                        <th>Section Point</th>
                                        <th>Distance</th>
                                        <th>Original Elevation</th>
                                        <th>Design Elevation</th>
                                        <th>Cut/Fill Height</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentObject.crossSectionPoints && currentObject.crossSectionPoints.map((crossSectionPoint: any, i: number) => (
                                        <tr
                                            key={'key-crossSectionPoint-' + i}
                                            onMouseEnter={() => vm.handleRowMouseEnter(crossSectionPoint)}
                                            onMouseLeave={vm.handleRowMouseLeave}
                                        >
                                            <td>
                                                <p
                                                    style={{ width: "80px" }}
                                                    aria-label={`sectionPoint-${i}`}
                                                    data-testid={`sectionPoint-${i}`}
                                                >{crossSectionPoint.sectionPoint}</p>
                                            </td>
                                            <td>
                                                <p
                                                    style={{ width: "80px" }}
                                                    aria-label={`pointDistance-${i}`}
                                                    data-testid={`pointDistance-${i}`}
                                                >{Number(crossSectionPoint.pointDistance).toFixed(1)}</p>
                                            </td>
                                            <td>
                                                <p
                                                    style={{ width: "80px" }}
                                                    aria-label={`originalElevation-${i}`}
                                                    data-testid={`originalElevation-${i}`}
                                                >{Number(crossSectionPoint.originalElevation).toFixed(1)}</p>
                                            </td>
                                            <td>
                                                <p
                                                    style={{ width: "80px" }}
                                                    aria-label={`designElevation-${i}`}
                                                    data-testid={`designElevation-${i}`}
                                                >{Number(crossSectionPoint.designElevation).toFixed(1)}</p>
                                            </td>
                                            <td>
                                                <p
                                                    style={{ width: "80px" }}
                                                    aria-label={`cutFillHeight-${i}`}
                                                    data-testid={`cutFillHeight-${i}`}
                                                >{Number(crossSectionPoint.cutFillHeight).toFixed(1)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    {vm.isSuccessful && (
                                        <tr>
                                            <td colSpan={4}>
                                                <Alert show={vm.isSuccessful} type="success" title={<SuccessMessage />} />
                                            </td>
                                        </tr>
                                    )}
                                    {vm.error && (
                                        <tr>
                                            <td colSpan={4}>
                                                <Alert show={vm.error} type="error" msg={vm.errorMsg} />
                                            </td>
                                        </tr>
                                    )}
                                </tfoot>

                            </Table>

                        </div>
                    </Accordion>
                    <div className="save-button-container single-save-button-container">
                        <button className="usa-button usa-button--outline" aria-label="Cancel" onClick={vm.onClickCancelCrossSec}>
                            Close
                        </button>
                        <button className="usa-button" aria-label="Save" disabled={!designStore.crossSectionDrawingComplete || designStore.isLoading} onClick={() => vm.onClickSaveCrossSec(profileStoreRef.current?._profileView, EngineeringMapService.instance?.mapView)}>
                            Save for Report
                        </button>
                    </div>
                </CardBody>
            </Card>
        </div>
    )
}
const SuccessMessage = () => (
    <>
        <div className="mt-1" style={{ whiteSpace: "nowrap", textAlign: "right" }}> The cross section is successfully saved</div>
    </>
);
export const SingleCrossSection = observer(SingleCrossSectionComponent);